/**
 * Browser Reveal Capture Module
 *
 * Captures window.reveal data that some clients have pre-installed.
 *
 * IMPORTANT: This is NOT a Reveal API integration.
 * Reveal is a third-party visitor identification tool that some of our clients
 * have already installed on their sites independently. When available, we simply
 * read the existing window.reveal browser variable to enhance visitor profiling.
 */

import { logger } from '../utils/core/logger';
import type { BrowserRevealData } from './types';

/**
 * Type guard to check if value is a non-null object
 */
function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * Normalize Reveal data to match expected backend schema.
 *
 * Different Reveal implementations may have different data structures.
 * This function ensures the data matches our backend API schema.
 *
 * @param reveal Raw Reveal data from window.reveal
 * @returns Normalized Reveal data matching BrowserRevealData schema
 */
function normalizeRevealData(reveal: unknown): BrowserRevealData | null {
    try {
        if (!isObject(reveal)) {
            return null;
        }

        const normalized: BrowserRevealData = {};

        // Copy simple string/boolean fields with type guards
        if (typeof reveal.ip === 'string') normalized.ip = reveal.ip;
        if (typeof reveal.domain === 'string') normalized.domain = reveal.domain;
        if (typeof reveal.type === 'string') normalized.type = reveal.type;
        if (typeof reveal.fuzzy === 'boolean') normalized.fuzzy = reveal.fuzzy;

        // Normalize company - handle both string and object formats
        if (reveal.company) {
            if (typeof reveal.company === 'string') {
                normalized.company = reveal.company;
            } else if (isObject(reveal.company) && typeof reveal.company.name === 'string') {
                // Extract company name from object structure
                normalized.company = reveal.company.name;
                logger.debug('[Browser Reveal] Normalized company object to string', {
                    from: 'object',
                    name: normalized.company,
                });
            }
        }

        // Normalize confidenceScore - handle string, number, and null
        if (reveal.confidenceScore !== null && reveal.confidenceScore !== undefined) {
            if (typeof reveal.confidenceScore === 'number') {
                // Already a number, ensure it's in valid range [0, 1]
                normalized.confidenceScore = Math.max(0, Math.min(1, reveal.confidenceScore));
            } else if (typeof reveal.confidenceScore === 'string') {
                // Convert string confidence levels to numbers
                const scoreMap: Record<string, number> = {
                    very_high: 0.95,
                    high: 0.8,
                    medium: 0.6,
                    low: 0.3,
                    very_low: 0.1,
                };
                const lowerScore = reveal.confidenceScore.toLowerCase();
                normalized.confidenceScore = scoreMap[lowerScore] ?? 0.5;
                logger.debug('[Browser Reveal] Normalized confidenceScore string to number', {
                    from: reveal.confidenceScore,
                    to: normalized.confidenceScore,
                });
            }
        }

        // Copy geoIP data with type guards
        if (isObject(reveal.geoIP)) {
            const geoIP = reveal.geoIP;
            normalized.geoIP = {
                city: typeof geoIP.city === 'string' ? geoIP.city : undefined,
                state: typeof geoIP.state === 'string' ? geoIP.state : undefined,
                stateCode: typeof geoIP.stateCode === 'string' ? geoIP.stateCode : undefined,
                country: typeof geoIP.country === 'string' ? geoIP.country : undefined,
                countryCode: typeof geoIP.countryCode === 'string' ? geoIP.countryCode : undefined,
            };
        }

        // Copy privacy-sensitive fields (not used but preserved)
        if (typeof reveal.role === 'string') normalized.role = reveal.role;
        if (typeof reveal.seniority === 'string') normalized.seniority = reveal.seniority;

        return normalized;
    } catch (error) {
        logger.error('[Browser Reveal] Error normalizing Reveal data', error);
        return null;
    }
}

/**
 * Attempt to capture Reveal data from browser window object.
 *
 * This function safely reads window.reveal if it exists. It returns null
 * for most sites (those without Reveal installed), which is normal.
 *
 * @returns Browser Reveal data if available, null otherwise
 */
export function captureWindowReveal(): BrowserRevealData | null {
    try {
        // Check if running in browser environment
        if (typeof window === 'undefined') {
            return null;
        }

        // Check if window.reveal exists (only on sites with Reveal pre-installed)
        const reveal = window.reveal;

        if (!reveal || typeof reveal !== 'object') {
            logger.debug('[Browser Reveal] window.reveal not found (normal for most sites)');
            return null;
        }

        // Validate it has the expected structure
        if (!reveal.ip && !reveal.company && !reveal.domain) {
            logger.debug('[Browser Reveal] window.reveal exists but has no usable data');
            return null;
        }

        logger.debug('[Browser Reveal] Raw browser Reveal data detected', {
            hasCompany: !!reveal.company,
            companyType: typeof reveal.company,
            type: reveal.type,
            confidenceScore: reveal.confidenceScore,
            confidenceScoreType: typeof reveal.confidenceScore,
            domain: reveal.domain,
        });

        // Normalize the data to match expected backend schema
        const normalized = normalizeRevealData(reveal);

        if (normalized) {
            logger.debug('[Browser Reveal] Browser Reveal data normalized successfully', {
                company: normalized.company,
                type: normalized.type,
                confidenceScore: normalized.confidenceScore,
                domain: normalized.domain,
            });
        }

        return normalized;
    } catch (error) {
        logger.debug('[Browser Reveal] Error reading window.reveal (normal for most sites)', error);
        return null;
    }
}
